// Auto-generated file - do not edit manually
// Generated by scripts/embed-templates.js

export const EMBEDDED_TEMPLATES: Record<string, string> = {
  'app-router/tasks-stream.ts': `import { NextRequest } from 'next/server';
import { FileScanner, TaskExtractor, CacheManager } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';
import { createStreamError, getRequestId } from '@/utils/api-errors';

export const runtime = 'nodejs';
export const dynamic = 'force-dynamic';

// Store active connections for broadcasting updates
const clients = new Map<string, ReadableStreamDefaultController>();

// Task change detection
let lastTaskHash: string | null = null;

export async function GET(request: NextRequest) {
  const encoder = new TextEncoder();
  const clientId = crypto.randomUUID();
  
  // Get query parameters
  const searchParams = request.nextUrl.searchParams;
  const filter = searchParams.get('filter'); // todo, fixme, etc.
  const includeCompleted = searchParams.get('includeCompleted') === 'true';
  
  const stream = new ReadableStream({
    async start(controller) {
      // Add client to active connections
      clients.set(clientId, controller);
      
      // Send initial connection message
      const connectMessage = \`data: \${JSON.stringify({ 
        type: 'connected', 
        clientId,
        timestamp: new Date().toISOString() 
      })}\\n\\n\`;
      controller.enqueue(encoder.encode(connectMessage));
      
      // Send initial tasks
      try {
        const tasks = await scanTasks(filter, includeCompleted);
        const taskMessage = \`data: \${JSON.stringify({
          type: 'tasks',
          tasks,
          timestamp: new Date().toISOString()
        })}\\n\\n\`;
        controller.enqueue(encoder.encode(taskMessage));
      } catch (error) {
        const errorMessage = createStreamError(error, getRequestId(request));
        controller.enqueue(encoder.encode(errorMessage));
      }
      
      // Set up polling for changes
      const pollInterval = setInterval(async () => {
        try {
          const tasks = await scanTasks(filter, includeCompleted);
          const currentHash = generateTaskHash(tasks);
          
          if (currentHash !== lastTaskHash) {
            lastTaskHash = currentHash;
            
            // Broadcast to all connected clients
            const updateMessage = \`data: \${JSON.stringify({
              type: 'update',
              tasks,
              timestamp: new Date().toISOString()
            })}\\n\\n\`;
            
            for (const [id, client] of clients.entries()) {
              try {
                client.enqueue(encoder.encode(updateMessage));
              } catch (error) {
                // Client disconnected, remove from map
                clients.delete(id);
              }
            }
          }
        } catch (error) {
          console.error('Error polling tasks:', error);
        }
      }, 5000); // Poll every 5 seconds
      
      // Keep connection alive with heartbeat
      const heartbeatInterval = setInterval(() => {
        try {
          const heartbeatMessage = \`data: \${JSON.stringify({ 
            type: 'heartbeat', 
            timestamp: new Date().toISOString() 
          })}\\n\\n\`;
          controller.enqueue(encoder.encode(heartbeatMessage));
        } catch (error) {
          // Client disconnected
          clearInterval(heartbeatInterval);
          clearInterval(pollInterval);
          clients.delete(clientId);
        }
      }, 30000); // Send heartbeat every 30 seconds
      
      // Clean up on close
      request.signal.addEventListener('abort', () => {
        clearInterval(heartbeatInterval);
        clearInterval(pollInterval);
        clients.delete(clientId);
        controller.close();
      });
    },
  });
  
  return new Response(stream, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache, no-transform',
      'Connection': 'keep-alive',
      'X-Accel-Buffering': 'no', // Disable Nginx buffering
    },
  });
}

// Helper function to scan tasks
async function scanTasks(filter?: string | null, includeCompleted = false) {
  const config = await getConfig();
  const scanner = new FileScanner(config);
  const extractor = new TaskExtractor();
  const cache = new CacheManager({ maxSize: 100, ttl: 60000 }); // 1 minute cache
  
  // Check cache first
  const cacheKey = \`tasks:\${filter || 'all'}:\${includeCompleted}\`;
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  // Scan files
  const files = await scanner.scanWithInfo();
  const allTasks = [];
  
  for (const file of files) {
    const tasks = extractor.extract(file.content, file.path, {
      includeContext: true,
      parseMetadata: true,
    });
    
    // Apply filter if specified
    const filteredTasks = filter 
      ? tasks.filter(task => task.type.toLowerCase() === filter.toLowerCase())
      : tasks;
      
    // Filter completed tasks if needed
    const finalTasks = includeCompleted
      ? filteredTasks
      : filteredTasks.filter(task => !task.metadata?.completed);
      
    allTasks.push(...finalTasks);
  }
  
  // Sort by priority and date
  allTasks.sort((a, b) => {
    // Priority first (high > medium > low)
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const aPriority = priorityOrder[a.metadata?.priority || 'low'];
    const bPriority = priorityOrder[b.metadata?.priority || 'low'];
    
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    
    // Then by date (newer first)
    const aDate = a.metadata?.createdAt || 0;
    const bDate = b.metadata?.createdAt || 0;
    return bDate - aDate;
  });
  
  // Cache results
  cache.set(cacheKey, allTasks);
  
  return allTasks;
}

// Generate hash for task comparison
function generateTaskHash(tasks: any[]): string {
  const taskString = JSON.stringify(tasks.map(t => ({
    id: t.id,
    type: t.type,
    text: t.text,
    completed: t.metadata?.completed
  })));
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < taskString.length; i++) {
    const char = taskString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return hash.toString(36);
}`,
  'app-router/ai-chat.ts': `import { NextRequest } from 'next/server';
import { FileScanner, MarkdownParser } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';

// Error handling utility
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export const runtime = 'edge'; // Use edge runtime for streaming

export async function POST(request: NextRequest) {
  try {
    const { messages, context, stream = true } = await request.json();
    
    if (!messages || !Array.isArray(messages)) {
      return new Response(
        JSON.stringify({ error: 'Messages array is required' }),
        { status: 400, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    // Get relevant context from markdown files
    const relevantContext = await getRelevantContext(context);
    
    // Prepare system message with context
    const systemMessage = {
      role: 'system',
      content: \`You are an AI assistant helping with a development project. 
\${relevantContext ? \`Here is relevant context from the project:\\n\\n\${relevantContext}\` : ''}

Please provide helpful, accurate, and concise responses. When discussing code, use proper markdown formatting.\`
    };
    
    const allMessages = [systemMessage, ...messages];
    
    if (!stream) {
      // Non-streaming response
      const response = await callAIProvider(allMessages, false);
      return new Response(
        JSON.stringify({ message: response }),
        { headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    // Streaming response
    const encoder = new TextEncoder();
    const responseStream = new ReadableStream({
      async start(controller) {
        try {
          // Call AI provider with streaming
          const aiStream = await callAIProvider(allMessages, true);
          
          // Process the stream
          const reader = aiStream.getReader();
          
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              // Send final message
              controller.enqueue(encoder.encode('data: [DONE]\\n\\n'));
              break;
            }
            
            // Format as SSE
            const sseMessage = \`data: \${JSON.stringify({
              type: 'content',
              content: value,
              timestamp: new Date().toISOString()
            })}\\n\\n\`;
            
            controller.enqueue(encoder.encode(sseMessage));
          }
        } catch (error) {
          // Send error message
          const errorMessage = \`data: \${JSON.stringify({
            type: 'error',
            error: getErrorMessage(error),
            timestamp: new Date().toISOString()
          })}\\n\\n\`;
          
          controller.enqueue(encoder.encode(errorMessage));
        } finally {
          controller.close();
        }
      }
    });
    
    return new Response(responseStream, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Connection': 'keep-alive',
      },
    });
  } catch (error) {
    console.error('AI chat error:', error);
    return new Response(
      JSON.stringify({ error: 'Internal server error' }),
      { status: 500, headers: { 'Content-Type': 'application/json' } }
    );
  }
}

// Get relevant context from markdown files
async function getRelevantContext(contextRequest?: {
  files?: string[];
  search?: string;
  limit?: number;
}): Promise<string> {
  if (!contextRequest) return '';
  
  const config = await getConfig();
  const scanner = new FileScanner(config);
  const parser = new MarkdownParser();
  
  let contexts: string[] = [];
  
  // Get specific files if requested
  if (contextRequest.files?.length) {
    for (const file of contextRequest.files) {
      try {
        const content = await scanner.readFile(file);
        const parsed = parser.parse(content);
        contexts.push(\`File: \${file}\\n\${parsed.content.slice(0, 1000)}\`);
      } catch (error) {
        console.error(\`Error reading file \${file}:\`, error);
      }
    }
  }
  
  // Search for relevant content
  if (contextRequest.search) {
    const files = await scanner.scan();
    const relevantFiles = files
      .filter((file: any) => file.endsWith('.md') || file.endsWith('.mdx'))
      .slice(0, contextRequest.limit || 5);
    
    for (const file of relevantFiles) {
      try {
        const content = await scanner.readFile(file);
        if (content.toLowerCase().includes(contextRequest.search.toLowerCase())) {
          const parsed = parser.parse(content);
          contexts.push(\`File: \${file}\\n\${parsed.content.slice(0, 500)}\`);
        }
      } catch (error) {
        console.error(\`Error searching file \${file}:\`, error);
      }
    }
  }
  
  return contexts.join('\\n\\n---\\n\\n');
}

// Placeholder for AI provider integration
async function callAIProvider(
  messages: any[], 
  stream: boolean
): Promise<any> {
  // This is where you would integrate with your AI provider
  // Examples: OpenAI, Anthropic, Azure OpenAI, etc.
  
  // For now, return a mock response
  if (!stream) {
    return "This is a placeholder response. Please integrate with your preferred AI provider.";
  }
  
  // Mock streaming response
  const mockStream = new ReadableStream({
    async start(controller) {
      const response = "This is a streaming placeholder response. Please integrate with your preferred AI provider.";
      const words = response.split(' ');
      
      for (const word of words) {
        controller.enqueue(word + ' ');
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      controller.close();
    }
  });
  
  return mockStream;
}

// Example integrations (commented out):

/*
// OpenAI Integration
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY,
});

async function callOpenAI(messages: any[], stream: boolean) {
  if (!stream) {
    const completion = await openai.chat.completions.create({
      model: "gpt-4",
      messages,
    });
    return completion.choices[0].message.content;
  }
  
  const stream = await openai.chat.completions.create({
    model: "gpt-4",
    messages,
    stream: true,
  });
  
  return new ReadableStream({
    async start(controller) {
      for await (const chunk of stream) {
        controller.enqueue(chunk.choices[0]?.delta?.content || '');
      }
      controller.close();
    }
  });
}
*/

/*
// Anthropic Integration
import Anthropic from '@anthropic-ai/sdk';

const anthropic = new Anthropic({
  apiKey: process.env.ANTHROPIC_API_KEY,
});

async function callAnthropic(messages: any[], stream: boolean) {
  const systemMessage = messages.find(m => m.role === 'system');
  const userMessages = messages.filter(m => m.role !== 'system');
  
  if (!stream) {
    const response = await anthropic.messages.create({
      model: "claude-3-opus-20240229",
      system: systemMessage?.content,
      messages: userMessages,
      max_tokens: 1024,
    });
    return response.content[0].text;
  }
  
  const stream = await anthropic.messages.create({
    model: "claude-3-opus-20240229",
    system: systemMessage?.content,
    messages: userMessages,
    max_tokens: 1024,
    stream: true,
  });
  
  return new ReadableStream({
    async start(controller) {
      for await (const chunk of stream) {
        if (chunk.type === 'content_block_delta') {
          controller.enqueue(chunk.delta.text);
        }
      }
      controller.close();
    }
  });
}
*/`,
  'app-router/files.ts': `import { NextRequest, NextResponse } from 'next/server';
import { FileScanner, PathValidator, Logger } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';
import { z } from 'zod';

// Request validation schemas
const ReadFileSchema = z.object({
  path: z.string().min(1),
  encoding: z.enum(['utf8', 'base64']).optional().default('utf8'),
});

const WriteFileSchema = z.object({
  path: z.string().min(1),
  content: z.string(),
  encoding: z.enum(['utf8', 'base64']).optional().default('utf8'),
});

const DeleteFileSchema = z.object({
  path: z.string().min(1),
});

const ListFilesSchema = z.object({
  path: z.string().optional(),
  pattern: z.string().optional(),
  recursive: z.boolean().optional().default(true),
});

// GET: Read file or list files
export async function GET(request: NextRequest) {
  const logger = new Logger({ prefix: 'FileOperations' });
  const requestId = getRequestId(request);
  
  return await withErrorHandling(async () => {
    const searchParams = request.nextUrl.searchParams;
    const path = searchParams.get('path');
    
    if (path) {
      // Read single file
      const result = ReadFileSchema.safeParse({ 
        path, 
        encoding: searchParams.get('encoding') || 'utf8' 
      });
      
      if (!result.success) {
        throw new ApiError(
          ErrorCodes.INVALID_PARAMETERS,
          400,
          {
            message: 'Invalid file read parameters',
            cause: 'File path or encoding parameter is invalid',
            solution: 'Provide a valid file path and encoding (utf8 or base64)',
            docs: 'https://vibeship.dev/docs/api/files#read-file',
            context: { validationErrors: result.error.flatten() },
          },
          undefined,
          requestId
        );
      }
      
      const config = await getConfig();
      const scanner = new FileScanner(config);
      const validator = new PathValidator(config.scanPaths);
      
      // Validate path security
      if (!validator.isValid(result.data.path)) {
        logger.warn(\`Blocked access to invalid path: \${result.data.path}\`);
        return NextResponse.json(
          { error: 'Access denied: Invalid path' },
          { status: 403 }
        );
      }
      
      try {
        const content = await scanner.readFile(result.data.path);
        
        // Handle binary files
        if (result.data.encoding === 'base64') {
          const buffer = Buffer.from(content);
          return NextResponse.json({
            path: result.data.path,
            content: buffer.toString('base64'),
            encoding: 'base64',
            size: buffer.length,
          });
        }
        
        return NextResponse.json({
          path: result.data.path,
          content,
          encoding: 'utf8',
          size: content.length,
          lines: content.split('\\n').length,
        });
      } catch (error) {
        logger.error(\`Failed to read file \${result.data.path}:\`, error);
        return NextResponse.json(
          { error: 'File not found' },
          { status: 404 }
        );
      }
    } else {
      // List files
      const result = ListFilesSchema.safeParse({
        path: searchParams.get('path') || '.',
        pattern: searchParams.get('pattern'),
        recursive: searchParams.get('recursive') === 'true',
      });
      
      if (!result.success) {
        throw new ApiError(
          ErrorCodes.INVALID_PARAMETERS,
          400,
          {
            message: 'Invalid file read parameters',
            cause: 'File path or encoding parameter is invalid',
            solution: 'Provide a valid file path and encoding (utf8 or base64)',
            docs: 'https://vibeship.dev/docs/api/files#read-file',
            context: { validationErrors: result.error.flatten() },
          },
          undefined,
          requestId
        );
      }
      
      const config = await getConfig();
      const scanner = new FileScanner(config);
      
      const files = await scanner.scan(result.data.path || undefined);
      
      // Get file info for each file
      const filesWithInfo = await Promise.all(
        files.map(async (file) => {
          try {
            const info = await scanner.getFileInfo(file);
            return {
              path: file,
              size: info.size,
              modified: info.mtime,
              created: info.birthtime,
              isDirectory: info.isDirectory(),
            };
          } catch (error) {
            return {
              path: file,
              error: 'Failed to get file info',
            };
          }
        })
      );
      
      return NextResponse.json({
        files: filesWithInfo,
        total: filesWithInfo.length,
      });
    }
  } catch (error) {
    logger.error('File operation error:', error);
    return NextResponse.json(
      { error: 'Internal server error' },
      { status: 500 }
    );
  }
}

// POST: Write file
export async function POST(request: NextRequest) {
  const logger = new Logger({ prefix: 'FileOperations' });
  
  try {
    const body = await request.json();
    const result = WriteFileSchema.safeParse(body);
    
    if (!result.success) {
      return NextResponse.json(
        { error: 'Invalid parameters', details: result.error.flatten() },
        { status: 400 }
      );
    }
    
    const config = await getConfig();
    const validator = new PathValidator({
      allowedPaths: config.scanPaths,
      basePath: process.cwd(),
    });
    
    // Validate path security
    if (!validator.isValid(result.data.path)) {
      logger.warn(\`Blocked write to invalid path: \${result.data.path}\`);
      return NextResponse.json(
        { error: 'Access denied: Invalid path' },
        { status: 403 }
      );
    }
    
    // Check if file writes are explicitly disabled
    const disableWrites = process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true';
    
    if (disableWrites) {
      logger.warn('File writes are disabled by VIBESHIP_DISABLE_FILE_WRITES environment variable');
      return NextResponse.json({
        success: false,
        error: 'File writing is disabled in this environment',
        path: result.data.path,
      }, { status: 403 });
    }
    
    // Implement file writing
    try {
      const scanner = new FileScanner(config);
      await scanner.writeFile(result.data.path, result.data.content);
      
      logger.info(\`Successfully wrote file: \${result.data.path}\`);
      
      return NextResponse.json({
        success: true,
        path: result.data.path,
        size: result.data.content.length,
      });
    } catch (writeError: any) {
      logger.error(\`Failed to write file \${result.data.path}:\`, writeError);
      return NextResponse.json(
        { error: 'Failed to write file', details: writeError.message },
        { status: 500 }
      );
    }
  } catch (error) {
    logger.error('File write error:', error);
    return NextResponse.json(
      { error: 'Internal server error' },
      { status: 500 }
    );
  }
}

// DELETE: Delete file
export async function DELETE(request: NextRequest) {
  const logger = new Logger({ prefix: 'FileOperations' });
  
  try {
    const body = await request.json();
    const result = DeleteFileSchema.safeParse(body);
    
    if (!result.success) {
      return NextResponse.json(
        { error: 'Invalid parameters', details: result.error.flatten() },
        { status: 400 }
      );
    }
    
    const config = await getConfig();
    const validator = new PathValidator({
      allowedPaths: config.scanPaths,
      basePath: process.cwd(),
    });
    
    // Validate path security
    if (!validator.isValid(result.data.path)) {
      logger.warn(\`Blocked delete of invalid path: \${result.data.path}\`);
      return NextResponse.json(
        { error: 'Access denied: Invalid path' },
        { status: 403 }
      );
    }
    
    // In production, implement proper file deletion
    // For now, return success with warning
    logger.info(\`File delete requested for: \${result.data.path}\`);
    
    return NextResponse.json({
      success: true,
      path: result.data.path,
      warning: 'File deletion is disabled in template. Implement proper file deletion with security checks.',
    });
  } catch (error) {
    logger.error('File delete error:', error);
    return NextResponse.json(
      { error: 'Internal server error' },
      { status: 500 }
    );
  }
}

// PUT: Update file (partial update)
export async function PUT(request: NextRequest) {
  const logger = new Logger({ prefix: 'FileOperations' });
  
  try {
    const body = await request.json();
    const { path, updates } = body;
    
    if (!path || !updates) {
      return NextResponse.json(
        { error: 'Path and updates are required' },
        { status: 400 }
      );
    }
    
    const config = await getConfig();
    const validator = new PathValidator({
      allowedPaths: config.scanPaths,
      basePath: process.cwd(),
    });
    
    // Validate path security
    if (!validator.isValid(path)) {
      logger.warn(\`Blocked update to invalid path: \${path}\`);
      return NextResponse.json(
        { error: 'Access denied: Invalid path' },
        { status: 403 }
      );
    }
    
    // In production, implement proper file updating
    // This could support operations like:
    // - Append to file
    // - Replace specific lines
    // - Update frontmatter
    // - etc.
    
    logger.info(\`File update requested for: \${path}\`);
    
    return NextResponse.json({
      success: true,
      path,
      warning: 'File updating is disabled in template. Implement proper file updating with security checks.',
    });
  } catch (error) {
    logger.error('File update error:', error);
    return NextResponse.json(
      { error: 'Internal server error' },
      { status: 500 }
    );
  }
}`,
  'app-router/tasks.ts': `import { NextRequest, NextResponse } from 'next/server';
import { z } from 'zod';
import { ApiError, ErrorCodes, ErrorFactory, getRequestId, withErrorHandling } from '@/utils/api-errors';
import { loadDependenciesWithFallbacks } from '@/utils/fallback-handlers';

// Request validation schema
const TaskQuerySchema = z.object({
  type: z.enum(['todo', 'fixme', 'hack', 'note', 'all']).optional().default('all'),
  status: z.enum(['pending', 'completed', 'all']).optional().default('pending'),
  priority: z.enum(['high', 'medium', 'low', 'all']).optional().default('all'),
  path: z.string().optional(),
  search: z.string().optional(),
  limit: z.number().min(1).max(1000).optional().default(100),
  offset: z.number().min(0).optional().default(0),
  sortBy: z.enum(['priority', 'date', 'type', 'path']).optional().default('priority'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

// GET: Scan and return tasks
export async function GET(request: NextRequest) {
  const requestId = getRequestId(request);
  
  return await withErrorHandling(async () => {
    // Parse query parameters
    const searchParams = Object.fromEntries(request.nextUrl.searchParams);
    const query = TaskQuerySchema.safeParse({
      ...searchParams,
      limit: searchParams.limit ? parseInt(searchParams.limit) : undefined,
      offset: searchParams.offset ? parseInt(searchParams.offset) : undefined,
    });
    
    if (!query.success) {
      const requestId = getRequestId(request);
      const validationError = new ApiError(
        ErrorCodes.INVALID_PARAMETERS,
        400,
        {
          message: 'Invalid query parameters provided',
          cause: 'One or more query parameters are invalid or missing',
          solution: 'Check the API documentation for correct parameter format and values',
          docs: 'https://vibeship.dev/docs/api/tasks#query-parameters',
          context: { validationErrors: query.error.flatten() },
        },
        undefined,
        requestId
      );
      return validationError.toResponse();
    }
    
    // Load all dependencies with fallbacks
    const deps = await loadDependenciesWithFallbacks(requestId);
    const { config, FileScanner, TaskExtractor, Logger, CacheManager } = deps;
    
    const logger = new Logger({ prefix: 'TaskScanning' });
    logger.info('Config loaded:', { scanPaths: config.scanPaths });
    
    const scanner = new FileScanner(config);
    const extractor = new TaskExtractor();
    const cache = new CacheManager({ maxSize: 100, ttl: 300000 }); // 5 minute cache
    
    // Generate cache key
    const cacheKey = \`tasks:\${JSON.stringify(query.data)}\`;
    const cached = cache.get(cacheKey);
    
    if (cached) {
      logger.info('Returning cached tasks');
      return NextResponse.json(cached);
    }
    
    // Scan files
    let scanOptions = undefined;
    if (query.data.path) {
      // Normalize the path - if it doesn't start with . or /, add ./
      let normalizedPath = query.data.path;
      if (!normalizedPath.startsWith('.') && !normalizedPath.startsWith('/')) {
        normalizedPath = \`./\${normalizedPath}\`;
      }
      scanOptions = { paths: [normalizedPath] };
      logger.info(\`Using normalized scan path: \${normalizedPath}\`);
    }
      
    const files = await scanner.scanWithInfo(scanOptions);
    logger.info(\`Scanned \${files.length} files\`);
    
    const allTasks = [];
    
    // Extract tasks from each file
    for (const file of files) {
      const tasks = extractor.extract(file.content, file.path, {
        includeContext: true,
        parseMetadata: true,
        contextLines: 3,
      });
      
      if (tasks.length > 0) {
        logger.info(\`Found \${tasks.length} tasks in \${file.path}\`);
      }
      
      allTasks.push(...tasks);
    }
    
    logger.info(\`Total tasks found: \${allTasks.length}\`);
    
    // Apply filters
    let filteredTasks = allTasks;
    
    // Filter by type
    if (query.data.type !== 'all') {
      filteredTasks = filteredTasks.filter(
        task => task.type.toLowerCase() === query.data.type
      );
    }
    
    // Filter by status
    if (query.data.status !== 'all') {
      const isCompleted = query.data.status === 'completed';
      filteredTasks = filteredTasks.filter(
        task => (task.metadata?.completed || false) === isCompleted
      );
    }
    
    // Filter by priority
    if (query.data.priority !== 'all') {
      filteredTasks = filteredTasks.filter(
        task => (task.metadata?.priority || 'low') === query.data.priority
      );
    }
    
    // Search filter
    if (query.data.search) {
      const searchLower = query.data.search.toLowerCase();
      filteredTasks = filteredTasks.filter(
        task => 
          task.text.toLowerCase().includes(searchLower) ||
          task.file.toLowerCase().includes(searchLower) ||
          task.context?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort tasks
    const sortedTasks = sortTasks(filteredTasks, query.data.sortBy, query.data.sortOrder);
    
    // Apply pagination
    const totalCount = sortedTasks.length;
    const paginatedTasks = sortedTasks.slice(
      query.data.offset,
      query.data.offset + query.data.limit
    );
    
    // Group by type and calculate stats
    const grouped = extractor.groupByType(filteredTasks);
    const stats = {
      total: totalCount,
      byType: Object.entries(grouped).map(([type, tasks]) => ({
        type,
        count: tasks.length,
        completed: tasks.filter(t => t.metadata?.completed).length,
      })),
      byPriority: {
        high: filteredTasks.filter(t => t.metadata?.priority === 'high').length,
        medium: filteredTasks.filter(t => t.metadata?.priority === 'medium').length,
        low: filteredTasks.filter(t => t.metadata?.priority === 'low').length,
      },
    };
    
    // Enhanced response format with file and path information
    const enhancedTasks = paginatedTasks.map(task => ({
      ...task,
      id: task.id || \`\${task.file}-\${task.line}\`,
      path: task.file, // Ensure path is included
      title: task.text, // Ensure title is included
      status: task.metadata?.completed ? 'completed' : 'pending',
    }));
    
    const response = {
      tasks: enhancedTasks,
      pagination: {
        total: totalCount,
        limit: query.data.limit,
        offset: query.data.offset,
        hasMore: query.data.offset + query.data.limit < totalCount,
      },
      stats,
      query: query.data,
    };
    
    // Cache the response
    cache.set(cacheKey, response);
    
    return NextResponse.json(response);
  }, requestId, { operation: 'taskScan' });
}

// POST: Update task status
export async function POST(request: NextRequest) {
  const requestId = getRequestId(request);
  
  return await withErrorHandling(async () => {
    // Load dependencies with fallbacks
    const deps = await loadDependenciesWithFallbacks(requestId);
    const { Logger } = deps;
    const logger = new Logger({ prefix: 'TaskUpdate' });
    const body = await request.json();
    const { taskId, updates } = body;
    
    if (!taskId || !updates) {
      throw ErrorFactory.invalidRequest(
        'taskId and updates are required',
        !taskId ? 'taskId' : 'updates',
        requestId
      );
    }
    
    // In a real implementation, you would:
    // 1. Find the task by ID
    // 2. Update the task in the source file
    // 3. Return the updated task
    
    logger.info(\`Task update requested for: \${taskId}\`);
    
    return NextResponse.json({
      success: true,
      taskId,
      updates,
      warning: 'Task updating is disabled in template. Implement proper task updating.',
    });
  }, requestId, { operation: 'taskUpdate', taskId });
}

// Helper function to sort tasks
function sortTasks(
  tasks: Task[],
  sortBy: string,
  sortOrder: 'asc' | 'desc'
): Task[] {
  const sorted = [...tasks].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortBy) {
      case 'priority':
        const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.metadata?.priority || 'low'] || 1;
        const bPriority = priorityOrder[b.metadata?.priority || 'low'] || 1;
        compareValue = aPriority - bPriority;
        break;
        
      case 'date':
        const aDate = a.metadata?.createdAt || 0;
        const bDate = b.metadata?.createdAt || 0;
        compareValue = aDate - bDate;
        break;
        
      case 'type':
        compareValue = a.type.localeCompare(b.type);
        break;
        
      case 'path':
        compareValue = a.file.localeCompare(b.file);
        break;
    }
    
    return sortOrder === 'asc' ? compareValue : -compareValue;
  });
  
  return sorted;
}

// Export metadata for Next.js
export const metadata = {
  title: 'Task Scanner API',
  description: 'Scan and manage tasks in your codebase',
};`,
  'pages-router/tasks-stream.ts': `import type { NextApiRequest, NextApiResponse } from 'next';
import { FileScanner, TaskExtractor, CacheManager } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';

// Store active connections
const clients = new Map<string, NextApiResponse>();
let lastTaskHash: string | null = null;

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  if (req.method !== 'GET') {
    res.setHeader('Allow', ['GET']);
    return res.status(405).end(\`Method \${req.method} Not Allowed\`);
  }
  
  // Set SSE headers
  res.setHeader('Content-Type', 'text/event-stream');
  res.setHeader('Cache-Control', 'no-cache, no-transform');
  res.setHeader('Connection', 'keep-alive');
  res.setHeader('X-Accel-Buffering', 'no');
  
  const clientId = crypto.randomUUID();
  const { filter, includeCompleted } = req.query;
  
  // Add client to active connections
  clients.set(clientId, res);
  
  // Send initial connection message
  res.write(\`data: \${JSON.stringify({ 
    type: 'connected', 
    clientId,
    timestamp: new Date().toISOString() 
  })}\\n\\n\`);
  
  // Send initial tasks
  try {
    const tasks = await scanTasks(
      filter as string | undefined, 
      includeCompleted === 'true'
    );
    res.write(\`data: \${JSON.stringify({
      type: 'tasks',
      tasks,
      timestamp: new Date().toISOString()
    })}\\n\\n\`);
  } catch (error) {
    res.write(\`data: \${JSON.stringify({
      type: 'error',
      error: error.message,
      timestamp: new Date().toISOString()
    })}\\n\\n\`);
  }
  
  // Set up polling for changes
  const pollInterval = setInterval(async () => {
    try {
      const tasks = await scanTasks(
        filter as string | undefined,
        includeCompleted === 'true'
      );
      const currentHash = generateTaskHash(tasks);
      
      if (currentHash !== lastTaskHash) {
        lastTaskHash = currentHash;
        
        // Broadcast to all connected clients
        const updateMessage = \`data: \${JSON.stringify({
          type: 'update',
          tasks,
          timestamp: new Date().toISOString()
        })}\\n\\n\`;
        
        for (const [id, client] of clients.entries()) {
          try {
            client.write(updateMessage);
          } catch (error) {
            // Client disconnected
            clients.delete(id);
          }
        }
      }
    } catch (error) {
      console.error('Error polling tasks:', error);
    }
  }, 5000);
  
  // Keep connection alive with heartbeat
  const heartbeatInterval = setInterval(() => {
    try {
      res.write(\`data: \${JSON.stringify({ 
        type: 'heartbeat', 
        timestamp: new Date().toISOString() 
      })}\\n\\n\`);
    } catch (error) {
      // Client disconnected
      clearInterval(heartbeatInterval);
      clearInterval(pollInterval);
      clients.delete(clientId);
    }
  }, 30000);
  
  // Handle client disconnect
  req.on('close', () => {
    clearInterval(heartbeatInterval);
    clearInterval(pollInterval);
    clients.delete(clientId);
    res.end();
  });
}

// Helper function to scan tasks
async function scanTasks(filter?: string, includeCompleted = false) {
  const config = await getConfig();
  const scanner = new FileScanner(config);
  const extractor = new TaskExtractor();
  const cache = new CacheManager({ maxSize: 100, ttl: 60000 });
  
  // Check cache first
  const cacheKey = \`tasks:\${filter || 'all'}:\${includeCompleted}\`;
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  // Scan files
  const files = await scanner.scanWithInfo();
  const allTasks = [];
  
  for (const file of files) {
    const tasks = extractor.extract(file.content, file.path, {
      includeContext: true,
      parseMetadata: true,
    });
    
    // Apply filter if specified
    const filteredTasks = filter 
      ? tasks.filter(task => task.type.toLowerCase() === filter.toLowerCase())
      : tasks;
      
    // Filter completed tasks if needed
    const finalTasks = includeCompleted
      ? filteredTasks
      : filteredTasks.filter(task => !task.metadata?.completed);
      
    allTasks.push(...finalTasks);
  }
  
  // Sort by priority and date
  allTasks.sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const aPriority = priorityOrder[a.metadata?.priority || 'low'];
    const bPriority = priorityOrder[b.metadata?.priority || 'low'];
    
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    
    const aDate = a.metadata?.createdAt || 0;
    const bDate = b.metadata?.createdAt || 0;
    return bDate - aDate;
  });
  
  // Cache results
  cache.set(cacheKey, allTasks);
  
  return allTasks;
}

// Generate hash for task comparison
function generateTaskHash(tasks: any[]): string {
  const taskString = JSON.stringify(tasks.map(t => ({
    id: t.id,
    type: t.type,
    text: t.text,
    completed: t.metadata?.completed
  })));
  
  let hash = 0;
  for (let i = 0; i < taskString.length; i++) {
    const char = taskString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return hash.toString(36);
}`,
  'pages-router/ai-chat.ts': `import type { NextApiRequest, NextApiResponse } from 'next';
import { FileScanner, MarkdownParser } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  if (req.method !== 'POST') {
    res.setHeader('Allow', ['POST']);
    return res.status(405).end(\`Method \${req.method} Not Allowed\`);
  }
  
  try {
    const { messages, context, stream = true } = req.body;
    
    if (!messages || !Array.isArray(messages)) {
      return res.status(400).json({ error: 'Messages array is required' });
    }
    
    // Get relevant context from markdown files
    const relevantContext = await getRelevantContext(context);
    
    // Prepare system message with context
    const systemMessage = {
      role: 'system',
      content: \`You are an AI assistant helping with a development project. 
\${relevantContext ? \`Here is relevant context from the project:\\n\\n\${relevantContext}\` : ''}

Please provide helpful, accurate, and concise responses. When discussing code, use proper markdown formatting.\`
    };
    
    const allMessages = [systemMessage, ...messages];
    
    if (!stream) {
      // Non-streaming response
      const response = await callAIProvider(allMessages, false);
      return res.status(200).json({ message: response });
    }
    
    // Set up SSE for streaming
    res.setHeader('Content-Type', 'text/event-stream');
    res.setHeader('Cache-Control', 'no-cache');
    res.setHeader('Connection', 'keep-alive');
    
    // Call AI provider with streaming
    const aiStream = await callAIProvider(allMessages, true);
    const reader = aiStream.getReader();
    
    // Process the stream
    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            res.write('data: [DONE]\\n\\n');
            res.end();
            break;
          }
          
          // Format as SSE
          const sseMessage = \`data: \${JSON.stringify({
            type: 'content',
            content: value,
            timestamp: new Date().toISOString()
          })}\\n\\n\`;
          
          res.write(sseMessage);
        }
      } catch (error) {
        const errorMessage = \`data: \${JSON.stringify({
          type: 'error',
          error: error.message,
          timestamp: new Date().toISOString()
        })}\\n\\n\`;
        
        res.write(errorMessage);
        res.end();
      }
    };
    
    // Handle client disconnect
    req.on('close', () => {
      reader.cancel();
      res.end();
    });
    
    processStream();
    
  } catch (error) {
    console.error('AI chat error:', error);
    
    if (!res.headersSent) {
      res.status(500).json({ error: 'Internal server error' });
    }
  }
}

// Get relevant context from markdown files
async function getRelevantContext(contextRequest?: {
  files?: string[];
  search?: string;
  limit?: number;
}): Promise<string> {
  if (!contextRequest) return '';
  
  const config = await getConfig();
  const scanner = new FileScanner(config);
  const parser = new MarkdownParser();
  
  let contexts: string[] = [];
  
  // Get specific files if requested
  if (contextRequest.files?.length) {
    for (const file of contextRequest.files) {
      try {
        const content = await scanner.readFile(file);
        const parsed = parser.parse(content);
        contexts.push(\`File: \${file}\\n\${parsed.content.slice(0, 1000)}\`);
      } catch (error) {
        console.error(\`Error reading file \${file}:\`, error);
      }
    }
  }
  
  // Search for relevant content
  if (contextRequest.search) {
    const files = await scanner.scan();
    const relevantFiles = files
      .filter(file => file.endsWith('.md') || file.endsWith('.mdx'))
      .slice(0, contextRequest.limit || 5);
    
    for (const file of relevantFiles) {
      try {
        const content = await scanner.readFile(file);
        if (content.toLowerCase().includes(contextRequest.search.toLowerCase())) {
          const parsed = parser.parse(content);
          contexts.push(\`File: \${file}\\n\${parsed.content.slice(0, 500)}\`);
        }
      } catch (error) {
        console.error(\`Error searching file \${file}:\`, error);
      }
    }
  }
  
  return contexts.join('\\n\\n---\\n\\n');
}

// Placeholder for AI provider integration
async function callAIProvider(
  messages: any[], 
  stream: boolean
): Promise<any> {
  // This is where you would integrate with your AI provider
  // Examples: OpenAI, Anthropic, Azure OpenAI, etc.
  
  // For now, return a mock response
  if (!stream) {
    return "This is a placeholder response. Please integrate with your preferred AI provider.";
  }
  
  // Mock streaming response
  const mockStream = new ReadableStream({
    async start(controller) {
      const response = "This is a streaming placeholder response. Please integrate with your preferred AI provider.";
      const words = response.split(' ');
      
      for (const word of words) {
        controller.enqueue(word + ' ');
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      controller.close();
    }
  });
  
  return mockStream;
}

// Configuration type for runtime configuration
export const config = {
  api: {
    bodyParser: {
      sizeLimit: '1mb',
    },
  },
};`,
  'pages-router/files.ts': `import type { NextApiRequest, NextApiResponse } from 'next';
import { FileScanner, PathValidator, Logger } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';
import { z } from 'zod';

// Request validation schemas
const ReadFileSchema = z.object({
  path: z.string().min(1),
  encoding: z.enum(['utf8', 'base64']).optional().default('utf8'),
});

const WriteFileSchema = z.object({
  path: z.string().min(1),
  content: z.string(),
  encoding: z.enum(['utf8', 'base64']).optional().default('utf8'),
});

const DeleteFileSchema = z.object({
  path: z.string().min(1),
});

const ListFilesSchema = z.object({
  path: z.string().optional(),
  pattern: z.string().optional(),
  recursive: z.boolean().optional().default(true),
});

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  const logger = new Logger({ prefix: 'FileOperations' });
  
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res, logger);
      case 'POST':
        return handlePost(req, res, logger);
      case 'PUT':
        return handlePut(req, res, logger);
      case 'DELETE':
        return handleDelete(req, res, logger);
      default:
        res.setHeader('Allow', ['GET', 'POST', 'PUT', 'DELETE']);
        return res.status(405).end(\`Method \${req.method} Not Allowed\`);
    }
  } catch (error) {
    logger.error('File operation error:', error);
    return res.status(500).json({ error: 'Internal server error' });
  }
}

// GET: Read file or list files
async function handleGet(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  const { path, encoding, pattern, recursive } = req.query;
  
  if (path && typeof path === 'string') {
    // Read single file
    const result = ReadFileSchema.safeParse({ 
      path, 
      encoding: encoding || 'utf8' 
    });
    
    if (!result.success) {
      return res.status(400).json({
        error: 'Invalid parameters',
        details: result.error.flatten()
      });
    }
    
    const config = await getConfig();
    const scanner = new FileScanner(config);
    const validator = new PathValidator({
      allowedPaths: config.scanPaths,
      basePath: process.cwd(),
    });
    
    // Validate path security
    if (!validator.isValid(result.data.path)) {
      logger.warn(\`Blocked access to invalid path: \${result.data.path}\`);
      return res.status(403).json({ error: 'Access denied: Invalid path' });
    }
    
    try {
      const content = await scanner.readFile(result.data.path);
      
      // Handle binary files
      if (result.data.encoding === 'base64') {
        const buffer = Buffer.from(content);
        return res.status(200).json({
          path: result.data.path,
          content: buffer.toString('base64'),
          encoding: 'base64',
          size: buffer.length,
        });
      }
      
      return res.status(200).json({
        path: result.data.path,
        content,
        encoding: 'utf8',
        size: content.length,
        lines: content.split('\\n').length,
      });
    } catch (error) {
      logger.error(\`Failed to read file \${result.data.path}:\`, error);
      return res.status(404).json({ error: 'File not found' });
    }
  } else {
    // List files
    const result = ListFilesSchema.safeParse({
      path: path || '.',
      pattern: pattern as string,
      recursive: recursive === 'true',
    });
    
    if (!result.success) {
      return res.status(400).json({
        error: 'Invalid parameters',
        details: result.error.flatten()
      });
    }
    
    const config = await getConfig();
    const scanner = new FileScanner(config);
    
    const files = await scanner.scan({
      paths: result.data.path ? [result.data.path] : undefined,
      include: result.data.pattern ? [result.data.pattern] : undefined,
    });
    
    // Get file info for each file
    const filesWithInfo = await Promise.all(
      files.map(async (file) => {
        try {
          const info = await scanner.getFileInfo(file);
          return {
            path: file,
            size: info.size,
            modified: info.mtime,
            created: info.birthtime,
            isDirectory: info.isDirectory(),
          };
        } catch (error) {
          return {
            path: file,
            error: 'Failed to get file info',
          };
        }
      })
    );
    
    return res.status(200).json({
      files: filesWithInfo,
      total: filesWithInfo.length,
    });
  }
}

// POST: Write file
async function handlePost(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  const result = WriteFileSchema.safeParse(req.body);
  
  if (!result.success) {
    return res.status(400).json({
      error: 'Invalid parameters',
      details: result.error.flatten()
    });
  }
  
  const config = await getConfig();
  const validator = new PathValidator({
    allowedPaths: config.scanPaths,
    basePath: process.cwd(),
  });
  
  // Validate path security
  if (!validator.isValid(result.data.path)) {
    logger.warn(\`Blocked write to invalid path: \${result.data.path}\`);
    return res.status(403).json({ error: 'Access denied: Invalid path' });
  }
  
  // In production, implement proper file writing
  // For now, return success with warning
  logger.info(\`File write requested for: \${result.data.path}\`);
  
  return res.status(200).json({
    success: true,
    path: result.data.path,
    warning: 'File writing is disabled in template. Implement proper file writing with security checks.',
  });
}

// PUT: Update file
async function handlePut(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  const { path, updates } = req.body;
  
  if (!path || !updates) {
    return res.status(400).json({ error: 'Path and updates are required' });
  }
  
  const config = await getConfig();
  const validator = new PathValidator({
    allowedPaths: config.scanPaths,
    basePath: process.cwd(),
  });
  
  // Validate path security
  if (!validator.isValid(path)) {
    logger.warn(\`Blocked update to invalid path: \${path}\`);
    return res.status(403).json({ error: 'Access denied: Invalid path' });
  }
  
  // In production, implement proper file updating
  logger.info(\`File update requested for: \${path}\`);
  
  return res.status(200).json({
    success: true,
    path,
    warning: 'File updating is disabled in template. Implement proper file updating with security checks.',
  });
}

// DELETE: Delete file
async function handleDelete(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  const result = DeleteFileSchema.safeParse(req.body);
  
  if (!result.success) {
    return res.status(400).json({
      error: 'Invalid parameters',
      details: result.error.flatten()
    });
  }
  
  const config = await getConfig();
  const validator = new PathValidator({
    allowedPaths: config.scanPaths,
    basePath: process.cwd(),
  });
  
  // Validate path security
  if (!validator.isValid(result.data.path)) {
    logger.warn(\`Blocked delete of invalid path: \${result.data.path}\`);
    return res.status(403).json({ error: 'Access denied: Invalid path' });
  }
  
  // In production, implement proper file deletion
  logger.info(\`File delete requested for: \${result.data.path}\`);
  
  return res.status(200).json({
    success: true,
    path: result.data.path,
    warning: 'File deletion is disabled in template. Implement proper file deletion with security checks.',
  });
}

// Configuration for API route
export const config = {
  api: {
    bodyParser: {
      sizeLimit: '10mb', // Adjust based on your needs
    },
  },
};`,
  'pages-router/tasks.ts': `import type { NextApiRequest, NextApiResponse } from 'next';
import { FileScanner, TaskExtractor, CacheManager, Logger } from '@vibeship/devtools/server';
import { getConfig } from '@/lib/vibeship-config';
import { z } from 'zod';

// Request validation schema
const TaskQuerySchema = z.object({
  type: z.enum(['todo', 'fixme', 'hack', 'note', 'all']).optional().default('all'),
  status: z.enum(['pending', 'completed', 'all']).optional().default('pending'),
  priority: z.enum(['high', 'medium', 'low', 'all']).optional().default('all'),
  path: z.string().optional(),
  search: z.string().optional(),
  limit: z.number().min(1).max(1000).optional().default(100),
  offset: z.number().min(0).optional().default(0),
  sortBy: z.enum(['priority', 'date', 'type', 'path']).optional().default('priority'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  const logger = new Logger({ prefix: 'TaskScanning' });
  
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res, logger);
      case 'POST':
        return handlePost(req, res, logger);
      default:
        res.setHeader('Allow', ['GET', 'POST']);
        return res.status(405).end(\`Method \${req.method} Not Allowed\`);
    }
  } catch (error) {
    logger.error('Task operation error:', error);
    return res.status(500).json({ error: 'Internal server error' });
  }
}

// GET: Scan and return tasks
async function handleGet(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  // Parse query parameters
  const query = TaskQuerySchema.safeParse({
    ...req.query,
    limit: req.query.limit ? parseInt(req.query.limit as string) : undefined,
    offset: req.query.offset ? parseInt(req.query.offset as string) : undefined,
    type: req.query.type || 'all',
    status: req.query.status || 'pending',
    priority: req.query.priority || 'all',
    sortBy: req.query.sortBy || 'priority',
    sortOrder: req.query.sortOrder || 'desc',
  });
  
  if (!query.success) {
    return res.status(400).json({
      error: 'Invalid parameters',
      details: query.error.flatten()
    });
  }
  
  const config = await getConfig();
  const scanner = new FileScanner(config);
  const extractor = new TaskExtractor();
  const cache = new CacheManager({ maxSize: 100, ttl: 300000 }); // 5 minute cache
  
  // Generate cache key
  const cacheKey = \`tasks:\${JSON.stringify(query.data)}\`;
  const cached = cache.get(cacheKey);
  
  if (cached) {
    logger.info('Returning cached tasks');
    return res.status(200).json(cached);
  }
  
  // Scan files
  const scanOptions = query.data.path 
    ? { paths: [query.data.path] }
    : undefined;
    
  const files = await scanner.scanWithInfo(scanOptions);
  const allTasks = [];
  
  // Extract tasks from each file
  for (const file of files) {
    const tasks = extractor.extract(file.content, file.path, {
      includeContext: true,
      parseMetadata: true,
      contextLines: 3,
    });
    
    allTasks.push(...tasks);
  }
  
  // Apply filters
  let filteredTasks = allTasks;
  
  // Filter by type
  if (query.data.type !== 'all') {
    filteredTasks = filteredTasks.filter(
      task => task.type.toLowerCase() === query.data.type
    );
  }
  
  // Filter by status
  if (query.data.status !== 'all') {
    const isCompleted = query.data.status === 'completed';
    filteredTasks = filteredTasks.filter(
      task => (task.metadata?.completed || false) === isCompleted
    );
  }
  
  // Filter by priority
  if (query.data.priority !== 'all') {
    filteredTasks = filteredTasks.filter(
      task => (task.metadata?.priority || 'low') === query.data.priority
    );
  }
  
  // Search filter
  if (query.data.search) {
    const searchLower = query.data.search.toLowerCase();
    filteredTasks = filteredTasks.filter(
      task => 
        task.text.toLowerCase().includes(searchLower) ||
        task.file.toLowerCase().includes(searchLower) ||
        task.context?.toLowerCase().includes(searchLower)
    );
  }
  
  // Sort tasks
  const sortedTasks = sortTasks(filteredTasks, query.data.sortBy, query.data.sortOrder);
  
  // Apply pagination
  const totalCount = sortedTasks.length;
  const paginatedTasks = sortedTasks.slice(
    query.data.offset,
    query.data.offset + query.data.limit
  );
  
  // Group by type and calculate stats
  const grouped = extractor.groupByType(filteredTasks);
  const stats = {
    total: totalCount,
    byType: Object.entries(grouped).map(([type, tasks]) => ({
      type,
      count: tasks.length,
      completed: tasks.filter(t => t.metadata?.completed).length,
    })),
    byPriority: {
      high: filteredTasks.filter(t => t.metadata?.priority === 'high').length,
      medium: filteredTasks.filter(t => t.metadata?.priority === 'medium').length,
      low: filteredTasks.filter(t => t.metadata?.priority === 'low').length,
    },
  };
  
  const response = {
    tasks: paginatedTasks,
    pagination: {
      total: totalCount,
      limit: query.data.limit,
      offset: query.data.offset,
      hasMore: query.data.offset + query.data.limit < totalCount,
    },
    stats,
    query: query.data,
  };
  
  // Cache the response
  cache.set(cacheKey, response);
  
  return res.status(200).json(response);
}

// POST: Update task status
async function handlePost(
  req: NextApiRequest,
  res: NextApiResponse,
  logger: Logger
) {
  const { taskId, updates } = req.body;
  
  if (!taskId || !updates) {
    return res.status(400).json({ 
      error: 'taskId and updates are required' 
    });
  }
  
  // In a real implementation, you would:
  // 1. Find the task by ID
  // 2. Update the task in the source file
  // 3. Return the updated task
  
  logger.info(\`Task update requested for: \${taskId}\`);
  
  return res.status(200).json({
    success: true,
    taskId,
    updates,
    warning: 'Task updating is disabled in template. Implement proper task updating.',
  });
}

// Helper function to sort tasks
function sortTasks(
  tasks: any[],
  sortBy: string,
  sortOrder: 'asc' | 'desc'
): any[] {
  const sorted = [...tasks].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortBy) {
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.metadata?.priority || 'low'];
        const bPriority = priorityOrder[b.metadata?.priority || 'low'];
        compareValue = aPriority - bPriority;
        break;
        
      case 'date':
        const aDate = a.metadata?.createdAt || 0;
        const bDate = b.metadata?.createdAt || 0;
        compareValue = aDate - bDate;
        break;
        
      case 'type':
        compareValue = a.type.localeCompare(b.type);
        break;
        
      case 'path':
        compareValue = a.file.localeCompare(b.file);
        break;
    }
    
    return sortOrder === 'asc' ? compareValue : -compareValue;
  });
  
  return sorted;
}

// Configuration for API route
export const config = {
  api: {
    bodyParser: {
      sizeLimit: '1mb',
    },
  },
};`,
  'middleware/auth.ts': `import { NextRequest, NextResponse } from 'next/server';
import { Logger } from '@vibeship/devtools/server';

// Types
export interface AuthConfig {
  secret?: string;
  apiKey?: string;
  allowedOrigins?: string[];
  requireAuth?: boolean;
  publicPaths?: string[];
}

export interface AuthUser {
  id: string;
  email?: string;
  role?: string;
  permissions?: string[];
}

// Default configuration
const defaultConfig: AuthConfig = {
  requireAuth: process.env.NODE_ENV === 'production',
  publicPaths: ['/api/vibeship/health', '/api/vibeship/docs'],
  allowedOrigins: process.env.ALLOWED_ORIGINS?.split(',') || ['http://localhost:3000'],
};

/**
 * Authentication middleware for Next.js App Router
 */
export function withAuth(
  handler: (req: NextRequest, context: any) => Promise<Response>,
  config: AuthConfig = {}
) {
  const authConfig = { ...defaultConfig, ...config };
  const logger = new Logger({ prefix: 'AuthMiddleware' });
  
  return async function authenticatedHandler(
    req: NextRequest,
    context: any
  ): Promise<Response> {
    try {
      // Check if path is public
      const pathname = req.nextUrl.pathname;
      if (authConfig.publicPaths?.some(path => pathname.startsWith(path))) {
        return handler(req, context);
      }
      
      // Skip auth in development if not required
      if (!authConfig.requireAuth) {
        logger.info('Auth skipped in development mode');
        return handler(req, context);
      }
      
      // Check authentication methods in order
      let user: AuthUser | null = null;
      
      // 1. Check API Key
      if (authConfig.apiKey) {
        const apiKey = req.headers.get('x-api-key');
        if (apiKey === authConfig.apiKey) {
          user = { id: 'api-key-user', role: 'api' };
        }
      }
      
      // 2. Check JWT Token
      if (!user && authConfig.secret) {
        const token = extractToken(req);
        if (token) {
          try {
            // Dynamic import for optional jsonwebtoken dependency
            const jwt = await import('jsonwebtoken').catch(() => null);
            if (jwt) {
              const decoded = jwt.verify(token, authConfig.secret) as AuthUser;
              user = decoded;
            } else {
              logger.warn('JWT authentication unavailable - jsonwebtoken package not installed');
            }
          } catch (error) {
            logger.warn('Invalid JWT token:', error.message);
          }
        }
      }
      
      // 3. Check Session (if using NextAuth or similar)
      if (!user) {
        user = await checkSession(req);
      }
      
      // If no valid auth found
      if (!user) {
        logger.warn(\`Unauthorized access attempt to \${pathname}\`);
        return NextResponse.json(
          { error: 'Unauthorized' },
          { status: 401 }
        );
      }
      
      // Add user to request headers for downstream use
      const headers = new Headers(req.headers);
      headers.set('x-user-id', user.id);
      headers.set('x-user-role', user.role || 'user');
      
      // Create new request with auth headers
      const authenticatedReq = new NextRequest(req.url, {
        headers,
        method: req.method,
        body: req.body,
      });
      
      // Call the actual handler
      return handler(authenticatedReq, context);
      
    } catch (error) {
      logger.error('Auth middleware error:', error);
      return NextResponse.json(
        { error: 'Internal server error' },
        { status: 500 }
      );
    }
  };
}

/**
 * Extract token from various sources
 */
function extractToken(req: NextRequest): string | null {
  // 1. Authorization header
  const authHeader = req.headers.get('authorization');
  if (authHeader?.startsWith('Bearer ')) {
    return authHeader.substring(7);
  }
  
  // 2. Cookie
  const cookieToken = req.cookies.get('auth-token')?.value;
  if (cookieToken) {
    return cookieToken;
  }
  
  // 3. Query parameter (not recommended for production)
  const searchParams = new URL(req.url).searchParams;
  const queryToken = searchParams.get('token');
  if (queryToken) {
    return queryToken;
  }
  
  return null;
}

/**
 * Check session (placeholder for NextAuth or similar)
 */
async function checkSession(req: NextRequest): Promise<AuthUser | null> {
  // Example: NextAuth.js integration
  /*
  try {
    const session = await getServerSession(authOptions);
    if (session?.user) {
      return {
        id: session.user.id,
        email: session.user.email,
        role: session.user.role,
      };
    }
  } catch (error) {
    console.error('Session check error:', error);
  }
  */
  
  return null;
}

/**
 * Utility to create API responses with auth headers
 */
export function createAuthResponse(
  data: any,
  options: {
    status?: number;
    headers?: HeadersInit;
    user?: AuthUser;
  } = {}
): NextResponse {
  const headers = new Headers(options.headers);
  
  // Add security headers
  headers.set('X-Content-Type-Options', 'nosniff');
  headers.set('X-Frame-Options', 'DENY');
  headers.set('X-XSS-Protection', '1; mode=block');
  
  // Add user info to response headers if provided
  if (options.user) {
    headers.set('X-User-ID', options.user.id);
  }
  
  return NextResponse.json(data, {
    status: options.status || 200,
    headers,
  });
}

/**
 * Permission checker utility
 */
export function hasPermission(
  user: AuthUser,
  requiredPermission: string | string[]
): boolean {
  if (!user.permissions) return false;
  
  const required = Array.isArray(requiredPermission) 
    ? requiredPermission 
    : [requiredPermission];
    
  return required.every(perm => user.permissions?.includes(perm));
}

/**
 * Role checker utility
 */
export function hasRole(
  user: AuthUser,
  requiredRole: string | string[]
): boolean {
  if (!user.role) return false;
  
  const required = Array.isArray(requiredRole) 
    ? requiredRole 
    : [requiredRole];
    
  return required.includes(user.role);
}`,
  'middleware/auth-pages.ts': `import type { NextApiRequest, NextApiResponse, NextApiHandler } from 'next';
import { Logger } from '@vibeship/devtools/server';

// Types
export interface AuthConfig {
  secret?: string;
  apiKey?: string;
  allowedOrigins?: string[];
  requireAuth?: boolean;
  publicPaths?: string[];
}

export interface AuthUser {
  id: string;
  email?: string;
  role?: string;
  permissions?: string[];
}

export interface AuthenticatedRequest extends NextApiRequest {
  user?: AuthUser;
}

// Default configuration
const defaultConfig: AuthConfig = {
  requireAuth: process.env.NODE_ENV === 'production',
  publicPaths: ['/api/vibeship/health', '/api/vibeship/docs'],
  allowedOrigins: process.env.ALLOWED_ORIGINS?.split(',') || ['http://localhost:3000'],
};

/**
 * Authentication middleware for Next.js Pages Router
 */
export function withAuth(
  handler: (req: AuthenticatedRequest, res: NextApiResponse) => Promise<void> | void,
  config: AuthConfig = {}
): NextApiHandler {
  const authConfig = { ...defaultConfig, ...config };
  const logger = new Logger({ prefix: 'AuthMiddleware' });
  
  return async function authenticatedHandler(
    req: AuthenticatedRequest,
    res: NextApiResponse
  ) {
    try {
      // Check if path is public
      const pathname = req.url || '';
      if (authConfig.publicPaths?.some(path => pathname.startsWith(path))) {
        return handler(req, res);
      }
      
      // Skip auth in development if not required
      if (!authConfig.requireAuth) {
        logger.info('Auth skipped in development mode');
        return handler(req, res);
      }
      
      // Check authentication methods in order
      let user: AuthUser | null = null;
      
      // 1. Check API Key
      if (authConfig.apiKey) {
        const apiKey = req.headers['x-api-key'];
        if (apiKey === authConfig.apiKey) {
          user = { id: 'api-key-user', role: 'api' };
        }
      }
      
      // 2. Check JWT Token
      if (!user && authConfig.secret) {
        const token = extractToken(req);
        if (token) {
          try {
            // Dynamic import for optional jsonwebtoken dependency
            const jwt = await import('jsonwebtoken').catch(() => null);
            if (jwt) {
              const decoded = jwt.verify(token, authConfig.secret) as AuthUser;
              user = decoded;
            } else {
              logger.warn('JWT authentication unavailable - jsonwebtoken package not installed');
            }
          } catch (error) {
            logger.warn('Invalid JWT token:', error.message);
          }
        }
      }
      
      // 3. Check Session (if using NextAuth or similar)
      if (!user) {
        user = await checkSession(req);
      }
      
      // If no valid auth found
      if (!user) {
        logger.warn(\`Unauthorized access attempt to \${pathname}\`);
        return res.status(401).json({ error: 'Unauthorized' });
      }
      
      // Attach user to request
      req.user = user;
      
      // Call the actual handler
      return handler(req, res);
      
    } catch (error) {
      logger.error('Auth middleware error:', error);
      return res.status(500).json({ error: 'Internal server error' });
    }
  };
}

/**
 * Extract token from various sources
 */
function extractToken(req: NextApiRequest): string | null {
  // 1. Authorization header
  const authHeader = req.headers.authorization;
  if (authHeader?.startsWith('Bearer ')) {
    return authHeader.substring(7);
  }
  
  // 2. Cookie
  const cookies = parseCookies(req.headers.cookie || '');
  const cookieToken = cookies['auth-token'];
  if (cookieToken) {
    return cookieToken;
  }
  
  // 3. Query parameter (not recommended for production)
  const queryToken = req.query.token as string;
  if (queryToken) {
    return queryToken;
  }
  
  return null;
}

/**
 * Parse cookie string
 */
function parseCookies(cookieString: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  
  cookieString.split(';').forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[name] = decodeURIComponent(value);
    }
  });
  
  return cookies;
}

/**
 * Check session (placeholder for NextAuth or similar)
 */
async function checkSession(req: NextApiRequest): Promise<AuthUser | null> {
  // Example: NextAuth.js integration
  /*
  try {
    const session = await getSession({ req });
    if (session?.user) {
      return {
        id: session.user.id,
        email: session.user.email,
        role: session.user.role,
      };
    }
  } catch (error) {
    console.error('Session check error:', error);
  }
  */
  
  return null;
}

/**
 * CORS middleware
 */
export function withCORS(
  handler: NextApiHandler,
  allowedOrigins: string[] = ['http://localhost:3000']
): NextApiHandler {
  return async (req, res) => {
    const origin = req.headers.origin;
    
    if (origin && allowedOrigins.includes(origin)) {
      res.setHeader('Access-Control-Allow-Origin', origin);
      res.setHeader('Access-Control-Allow-Credentials', 'true');
    }
    
    if (req.method === 'OPTIONS') {
      res.setHeader('Access-Control-Allow-Methods', 'GET, POST, PUT, DELETE, OPTIONS');
      res.setHeader('Access-Control-Allow-Headers', 'X-Requested-With, Content-Type, Authorization, X-API-Key');
      res.setHeader('Access-Control-Max-Age', '86400');
      return res.status(200).end();
    }
    
    return handler(req, res);
  };
}

/**
 * Combine multiple middleware
 */
export function composeMiddleware(
  ...middlewares: Array<(handler: NextApiHandler) => NextApiHandler>
): (handler: NextApiHandler) => NextApiHandler {
  return (handler: NextApiHandler) => {
    return middlewares.reduceRight((acc, middleware) => middleware(acc), handler);
  };
}

/**
 * Permission checker utility
 */
export function hasPermission(
  user: AuthUser,
  requiredPermission: string | string[]
): boolean {
  if (!user.permissions) return false;
  
  const required = Array.isArray(requiredPermission) 
    ? requiredPermission 
    : [requiredPermission];
    
  return required.every(perm => user.permissions?.includes(perm));
}

/**
 * Role checker utility
 */
export function hasRole(
  user: AuthUser,
  requiredRole: string | string[]
): boolean {
  if (!user.role) return false;
  
  const required = Array.isArray(requiredRole) 
    ? requiredRole 
    : [requiredRole];
    
  return required.includes(user.role);
}

/**
 * Example usage:
 * 
 * export default composeMiddleware(
 *   withCORS,
 *   withAuth
 * )(async (req: AuthenticatedRequest, res) => {
 *   const user = req.user!;
 *   res.status(200).json({ user });
 * });
 */`,
  'utils/error-handler.ts': `import { NextRequest, NextResponse } from 'next/server';
import { NextApiRequest, NextApiResponse } from 'next';
import { Logger } from '@vibeship/devtools/server';
import { ZodError } from 'zod';

// Custom error types
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ValidationError extends ApiError {
  constructor(message: string, details?: any) {
    super(400, message, details);
    this.name = 'ValidationError';
  }
}

export class AuthenticationError extends ApiError {
  constructor(message = 'Authentication required') {
    super(401, message);
    this.name = 'AuthenticationError';
  }
}

export class AuthorizationError extends ApiError {
  constructor(message = 'Insufficient permissions') {
    super(403, message);
    this.name = 'AuthorizationError';
  }
}

export class NotFoundError extends ApiError {
  constructor(resource: string) {
    super(404, \`\${resource} not found\`);
    this.name = 'NotFoundError';
  }
}

export class RateLimitError extends ApiError {
  constructor(retryAfter?: number) {
    super(429, 'Too many requests', { retryAfter });
    this.name = 'RateLimitError';
  }
}

// Error response format
interface ErrorResponse {
  error: {
    code: string;
    message: string;
    details?: any;
    timestamp: string;
    requestId?: string;
  };
}

/**
 * Error handler for App Router
 */
export function withErrorHandler(
  handler: (req: NextRequest, context: any) => Promise<Response>
) {
  const logger = new Logger({ prefix: 'ErrorHandler' });
  
  return async function errorHandledRoute(
    req: NextRequest,
    context: any
  ): Promise<Response> {
    const requestId = crypto.randomUUID();
    
    try {
      // Add request ID to headers for tracing
      const headers = new Headers(req.headers);
      headers.set('x-request-id', requestId);
      
      const modifiedReq = new NextRequest(req.url, {
        headers,
        method: req.method,
        body: req.body,
      });
      
      const response = await handler(modifiedReq, context);
      
      // Add request ID to response
      const responseHeaders = new Headers(response.headers);
      responseHeaders.set('x-request-id', requestId);
      
      return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
      });
      
    } catch (error) {
      logger.error(\`Request \${requestId} failed:\`, error);
      
      const errorResponse = formatError(error, requestId);
      
      // Send error metrics/alerts in production
      if (process.env.NODE_ENV === 'production') {
        sendErrorMetrics(error, req, requestId);
      }
      
      return NextResponse.json(errorResponse, {
        status: errorResponse.error.code === 'INTERNAL_ERROR' ? 500 : getStatusCode(error),
        headers: {
          'x-request-id': requestId,
        },
      });
    }
  };
}

/**
 * Error handler for Pages Router
 */
export function withErrorHandlerPages(
  handler: (req: NextApiRequest, res: NextApiResponse) => Promise<void> | void
) {
  const logger = new Logger({ prefix: 'ErrorHandler' });
  
  return async function errorHandledRoute(
    req: NextApiRequest,
    res: NextApiResponse
  ) {
    const requestId = crypto.randomUUID();
    
    // Add request ID to response headers
    res.setHeader('x-request-id', requestId);
    
    try {
      await handler(req, res);
    } catch (error) {
      logger.error(\`Request \${requestId} failed:\`, error);
      
      const errorResponse = formatError(error, requestId);
      
      // Send error metrics/alerts in production
      if (process.env.NODE_ENV === 'production') {
        sendErrorMetrics(error, req, requestId);
      }
      
      const statusCode = errorResponse.error.code === 'INTERNAL_ERROR' ? 500 : getStatusCode(error);
      
      if (!res.headersSent) {
        res.status(statusCode).json(errorResponse);
      }
    }
  };
}

/**
 * Format error for response
 */
function formatError(error: any, requestId: string): ErrorResponse {
  // Handle Zod validation errors
  if (error instanceof ZodError) {
    return {
      error: {
        code: 'VALIDATION_ERROR',
        message: 'Invalid request data',
        details: error.flatten(),
        timestamp: new Date().toISOString(),
        requestId,
      },
    };
  }
  
  // Handle custom API errors
  if (error instanceof ApiError) {
    return {
      error: {
        code: error.name.toUpperCase().replace('ERROR', '_ERROR'),
        message: error.message,
        details: error.details,
        timestamp: new Date().toISOString(),
        requestId,
      },
    };
  }
  
  // Handle known error types
  const errorMap: Record<string, { code: string; message: string }> = {
    SyntaxError: { code: 'SYNTAX_ERROR', message: 'Invalid JSON in request body' },
    TypeError: { code: 'TYPE_ERROR', message: 'Invalid data type' },
    RangeError: { code: 'RANGE_ERROR', message: 'Value out of range' },
  };
  
  const mappedError = errorMap[error.name];
  if (mappedError) {
    return {
      error: {
        code: mappedError.code,
        message: mappedError.message,
        details: error.message,
        timestamp: new Date().toISOString(),
        requestId,
      },
    };
  }
  
  // Default error response
  return {
    error: {
      code: 'INTERNAL_ERROR',
      message: process.env.NODE_ENV === 'production' 
        ? 'An unexpected error occurred' 
        : error.message,
      timestamp: new Date().toISOString(),
      requestId,
    },
  };
}

/**
 * Get HTTP status code from error
 */
function getStatusCode(error: any): number {
  if (error instanceof ApiError) {
    return error.statusCode;
  }
  
  if (error instanceof ZodError) {
    return 400;
  }
  
  const statusMap: Record<string, number> = {
    SyntaxError: 400,
    TypeError: 400,
    RangeError: 400,
  };
  
  return statusMap[error.name] || 500;
}

/**
 * Send error metrics (placeholder)
 */
function sendErrorMetrics(error: any, req: any, requestId: string) {
  // In production, integrate with your monitoring service
  // Examples: Sentry, DataDog, New Relic, etc.
  
  /*
  Sentry.captureException(error, {
    tags: {
      requestId,
      endpoint: req.url,
      method: req.method,
    },
    extra: {
      headers: req.headers,
      query: req.query,
    },
  });
  */
  
  console.error(\`[ERROR_METRICS] \${requestId}:\`, {
    error: error.message,
    stack: error.stack,
    endpoint: req.url,
    method: req.method,
  });
}

/**
 * Async error wrapper utility
 */
export function asyncHandler<T extends (...args: any[]) => Promise<any>>(
  fn: T
): T {
  return ((...args) => {
    return fn(...args).catch((error) => {
      throw error;
    });
  }) as T;
}

/**
 * Try-catch wrapper with typed errors
 */
export async function tryCatch<T, E = Error>(
  fn: () => Promise<T>,
  errorHandler?: (error: E) => T | Promise<T>
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (errorHandler) {
      return errorHandler(error as E);
    }
    throw error;
  }
}`,
  'utils/cors.ts': `import { NextRequest, NextResponse } from 'next/server';
import { NextApiRequest, NextApiResponse } from 'next';

// CORS configuration type
export interface CORSConfig {
  allowedOrigins?: string[] | string | ((origin: string) => boolean);
  allowedMethods?: string[];
  allowedHeaders?: string[];
  exposedHeaders?: string[];
  credentials?: boolean;
  maxAge?: number;
  optionsSuccessStatus?: number;
}

// Default CORS configuration
const defaultConfig: CORSConfig = {
  allowedOrigins: process.env.NODE_ENV === 'production' 
    ? (process.env.ALLOWED_ORIGINS?.split(',') || ['https://yourdomain.com'])
    : ['http://localhost:3000', 'http://localhost:3001'],
  allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'],
  allowedHeaders: ['Content-Type', 'Authorization', 'X-Requested-With', 'X-API-Key'],
  exposedHeaders: ['X-Request-ID', 'X-RateLimit-Limit', 'X-RateLimit-Remaining'],
  credentials: true,
  maxAge: 86400, // 24 hours
  optionsSuccessStatus: 204,
};

/**
 * CORS middleware for App Router
 */
export function corsMiddleware(config: CORSConfig = {}) {
  const corsConfig = { ...defaultConfig, ...config };
  
  return function cors(req: NextRequest): NextResponse | null {
    const origin = req.headers.get('origin');
    const response = new NextResponse(null, { status: 200 });
    
    // Check if origin is allowed
    if (origin && isOriginAllowed(origin, corsConfig.allowedOrigins)) {
      response.headers.set('Access-Control-Allow-Origin', origin);
      response.headers.set('Vary', 'Origin');
    } else if (!corsConfig.allowedOrigins || corsConfig.allowedOrigins === '*') {
      response.headers.set('Access-Control-Allow-Origin', '*');
    }
    
    // Set credentials
    if (corsConfig.credentials) {
      response.headers.set('Access-Control-Allow-Credentials', 'true');
    }
    
    // Handle preflight requests
    if (req.method === 'OPTIONS') {
      // Allowed methods
      response.headers.set(
        'Access-Control-Allow-Methods',
        corsConfig.allowedMethods?.join(', ') || ''
      );
      
      // Allowed headers
      response.headers.set(
        'Access-Control-Allow-Headers',
        corsConfig.allowedHeaders?.join(', ') || ''
      );
      
      // Max age
      if (corsConfig.maxAge) {
        response.headers.set('Access-Control-Max-Age', corsConfig.maxAge.toString());
      }
      
      return new NextResponse(null, { 
        status: corsConfig.optionsSuccessStatus || 204,
        headers: response.headers,
      });
    }
    
    // Set exposed headers for actual requests
    if (corsConfig.exposedHeaders?.length) {
      response.headers.set(
        'Access-Control-Expose-Headers',
        corsConfig.exposedHeaders.join(', ')
      );
    }
    
    return null; // Continue to the actual handler
  };
}

/**
 * CORS wrapper for App Router handlers
 */
export function withCORS(
  handler: (req: NextRequest, context: any) => Promise<Response>,
  config: CORSConfig = {}
) {
  const corsHandler = corsMiddleware(config);
  
  return async function corsWrappedHandler(
    req: NextRequest,
    context: any
  ): Promise<Response> {
    // Handle CORS preflight
    const corsResponse = corsHandler(req);
    if (corsResponse) {
      return corsResponse;
    }
    
    // Execute the actual handler
    const response = await handler(req, context);
    
    // Apply CORS headers to the response
    const origin = req.headers.get('origin');
    const corsConfig = { ...defaultConfig, ...config };
    
    if (origin && isOriginAllowed(origin, corsConfig.allowedOrigins)) {
      response.headers.set('Access-Control-Allow-Origin', origin);
      response.headers.set('Vary', 'Origin');
    } else if (!corsConfig.allowedOrigins || corsConfig.allowedOrigins === '*') {
      response.headers.set('Access-Control-Allow-Origin', '*');
    }
    
    if (corsConfig.credentials) {
      response.headers.set('Access-Control-Allow-Credentials', 'true');
    }
    
    if (corsConfig.exposedHeaders?.length) {
      response.headers.set(
        'Access-Control-Expose-Headers',
        corsConfig.exposedHeaders.join(', ')
      );
    }
    
    return response;
  };
}

/**
 * CORS middleware for Pages Router
 */
export function corsPages(config: CORSConfig = {}) {
  const corsConfig = { ...defaultConfig, ...config };
  
  return function cors(
    req: NextApiRequest,
    res: NextApiResponse,
    next?: () => void
  ) {
    const origin = req.headers.origin;
    
    // Check if origin is allowed
    if (origin && isOriginAllowed(origin, corsConfig.allowedOrigins)) {
      res.setHeader('Access-Control-Allow-Origin', origin);
      res.setHeader('Vary', 'Origin');
    } else if (!corsConfig.allowedOrigins || corsConfig.allowedOrigins === '*') {
      res.setHeader('Access-Control-Allow-Origin', '*');
    }
    
    // Set credentials
    if (corsConfig.credentials) {
      res.setHeader('Access-Control-Allow-Credentials', 'true');
    }
    
    // Handle preflight requests
    if (req.method === 'OPTIONS') {
      // Allowed methods
      res.setHeader(
        'Access-Control-Allow-Methods',
        corsConfig.allowedMethods?.join(', ') || ''
      );
      
      // Allowed headers
      res.setHeader(
        'Access-Control-Allow-Headers',
        corsConfig.allowedHeaders?.join(', ') || ''
      );
      
      // Max age
      if (corsConfig.maxAge) {
        res.setHeader('Access-Control-Max-Age', corsConfig.maxAge.toString());
      }
      
      res.status(corsConfig.optionsSuccessStatus || 204).end();
      return;
    }
    
    // Set exposed headers
    if (corsConfig.exposedHeaders?.length) {
      res.setHeader(
        'Access-Control-Expose-Headers',
        corsConfig.exposedHeaders.join(', ')
      );
    }
    
    if (next) {
      next();
    }
  };
}

/**
 * Check if origin is allowed
 */
function isOriginAllowed(
  origin: string,
  allowed?: string[] | string | ((origin: string) => boolean)
): boolean {
  if (!allowed) return false;
  
  if (typeof allowed === 'function') {
    return allowed(origin);
  }
  
  if (typeof allowed === 'string') {
    return allowed === '*' || allowed === origin;
  }
  
  return allowed.includes(origin);
}

/**
 * Dynamic CORS configuration based on environment
 */
export function createDynamicCORS(): CORSConfig {
  const isDevelopment = process.env.NODE_ENV !== 'production';
  
  return {
    allowedOrigins: (origin: string) => {
      // Always allow localhost in development
      if (isDevelopment && origin.includes('localhost')) {
        return true;
      }
      
      // Check against allowed domains
      const allowedDomains = process.env.ALLOWED_DOMAINS?.split(',') || [];
      return allowedDomains.some(domain => origin.includes(domain));
    },
    credentials: true,
    maxAge: isDevelopment ? 60 : 86400, // 1 minute in dev, 24 hours in prod
  };
}

/**
 * Strict CORS for production
 */
export function createStrictCORS(allowedDomains: string[]): CORSConfig {
  return {
    allowedOrigins: allowedDomains,
    allowedMethods: ['GET', 'POST'],
    allowedHeaders: ['Content-Type', 'Authorization'],
    credentials: true,
    maxAge: 3600, // 1 hour
  };
}`,
  'utils/rate-limit.ts': `import { NextRequest, NextResponse } from 'next/server';
import { NextApiRequest, NextApiResponse } from 'next';
import { RateLimiter } from '@vibeship/devtools/server';
import { RateLimitError } from './error-handler';

// Rate limit configuration
export interface RateLimitConfig {
  windowMs?: number;       // Time window in milliseconds
  maxRequests?: number;    // Max requests per window
  keyGenerator?: (req: NextRequest | NextApiRequest) => string;
  skipSuccessfulRequests?: boolean;
  skipFailedRequests?: boolean;
  message?: string;
  standardHeaders?: boolean;
  legacyHeaders?: boolean;
  store?: RateLimitStore;
}

// Rate limit store interface
export interface RateLimitStore {
  increment(key: string): Promise<{ count: number; resetTime: number }>;
  decrement(key: string): Promise<void>;
  reset(key: string): Promise<void>;
  clear(): Promise<void>;
}

// In-memory store implementation
export class MemoryStore implements RateLimitStore {
  private store = new Map<string, { count: number; resetTime: number }>();
  private timers = new Map<string, NodeJS.Timeout>();
  
  constructor(private windowMs: number) {}
  
  async increment(key: string): Promise<{ count: number; resetTime: number }> {
    const now = Date.now();
    const record = this.store.get(key);
    
    if (!record || now > record.resetTime) {
      const resetTime = now + this.windowMs;
      this.store.set(key, { count: 1, resetTime });
      
      // Set timer to clean up expired entries
      const timer = setTimeout(() => {
        this.store.delete(key);
        this.timers.delete(key);
      }, this.windowMs);
      
      this.timers.set(key, timer);
      
      return { count: 1, resetTime };
    }
    
    record.count++;
    return record;
  }
  
  async decrement(key: string): Promise<void> {
    const record = this.store.get(key);
    if (record && record.count > 0) {
      record.count--;
    }
  }
  
  async reset(key: string): Promise<void> {
    this.store.delete(key);
    const timer = this.timers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(key);
    }
  }
  
  async clear(): Promise<void> {
    this.store.clear();
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();
  }
}

// Default configuration
const defaultConfig: RateLimitConfig = {
  windowMs: 60 * 1000,        // 1 minute
  maxRequests: 100,           // 100 requests per minute
  skipSuccessfulRequests: false,
  skipFailedRequests: false,
  message: 'Too many requests, please try again later',
  standardHeaders: true,
  legacyHeaders: false,
};

/**
 * Default key generator
 */
function defaultKeyGenerator(req: NextRequest | NextApiRequest): string {
  // For Next.js App Router
  if ('nextUrl' in req) {
    const ip = req.headers.get('x-forwarded-for')?.split(',')[0].trim() || 
               req.headers.get('x-real-ip') || 
               'unknown';
    const userId = req.headers.get('x-user-id');
    return userId || ip;
  }
  
  // For Pages Router
  const ip = (req.headers['x-forwarded-for'] as string)?.split(',')[0].trim() || 
             req.headers['x-real-ip'] || 
             req.socket?.remoteAddress || 
             'unknown';
  
  // Check for authenticated user
  const userId = req.headers['x-user-id'];
  return userId || ip;
}

/**
 * Rate limiter for App Router
 */
export function withRateLimit(
  handler: (req: NextRequest, context: any) => Promise<Response>,
  config: RateLimitConfig = {}
) {
  const rateLimitConfig = { ...defaultConfig, ...config };
  const store = rateLimitConfig.store || new MemoryStore(rateLimitConfig.windowMs!);
  const keyGenerator = rateLimitConfig.keyGenerator || defaultKeyGenerator;
  
  return async function rateLimitedHandler(
    req: NextRequest,
    context: any
  ): Promise<Response> {
    const key = keyGenerator(req);
    const { count, resetTime } = await store.increment(key);
    
    const remaining = Math.max(0, rateLimitConfig.maxRequests! - count);
    const isRateLimited = count > rateLimitConfig.maxRequests!;
    
    // Create response headers
    const headers = new Headers();
    
    if (rateLimitConfig.standardHeaders) {
      headers.set('RateLimit-Limit', rateLimitConfig.maxRequests!.toString());
      headers.set('RateLimit-Remaining', remaining.toString());
      headers.set('RateLimit-Reset', new Date(resetTime).toISOString());
    }
    
    if (rateLimitConfig.legacyHeaders) {
      headers.set('X-RateLimit-Limit', rateLimitConfig.maxRequests!.toString());
      headers.set('X-RateLimit-Remaining', remaining.toString());
      headers.set('X-RateLimit-Reset', Math.floor(resetTime / 1000).toString());
    }
    
    // If rate limited, return 429 response
    if (isRateLimited) {
      const retryAfter = Math.ceil((resetTime - Date.now()) / 1000);
      headers.set('Retry-After', retryAfter.toString());
      
      return NextResponse.json(
        {
          error: {
            code: 'RATE_LIMIT_EXCEEDED',
            message: rateLimitConfig.message,
            retryAfter,
          },
        },
        { status: 429, headers }
      );
    }
    
    try {
      // Execute the handler
      const response = await handler(req, context);
      
      // Add rate limit headers to successful response
      const responseHeaders = new Headers(response.headers);
      headers.forEach((value, key) => {
        responseHeaders.set(key, value);
      });
      
      // Optionally skip counting successful requests
      if (rateLimitConfig.skipSuccessfulRequests && response.ok) {
        await store.decrement(key);
      }
      
      return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
      });
      
    } catch (error) {
      // Optionally skip counting failed requests
      if (rateLimitConfig.skipFailedRequests) {
        await store.decrement(key);
      }
      
      throw error;
    }
  };
}

/**
 * Rate limiter for Pages Router
 */
export function rateLimitPages(config: RateLimitConfig = {}) {
  const rateLimitConfig = { ...defaultConfig, ...config };
  const store = rateLimitConfig.store || new MemoryStore(rateLimitConfig.windowMs!);
  const keyGenerator = rateLimitConfig.keyGenerator || defaultKeyGenerator;
  
  return async function rateLimit(
    req: NextApiRequest,
    res: NextApiResponse,
    next?: () => void | Promise<void>
  ) {
    const key = keyGenerator(req);
    const { count, resetTime } = await store.increment(key);
    
    const remaining = Math.max(0, rateLimitConfig.maxRequests! - count);
    const isRateLimited = count > rateLimitConfig.maxRequests!;
    
    // Set headers
    if (rateLimitConfig.standardHeaders) {
      res.setHeader('RateLimit-Limit', rateLimitConfig.maxRequests!);
      res.setHeader('RateLimit-Remaining', remaining);
      res.setHeader('RateLimit-Reset', new Date(resetTime).toISOString());
    }
    
    if (rateLimitConfig.legacyHeaders) {
      res.setHeader('X-RateLimit-Limit', rateLimitConfig.maxRequests!);
      res.setHeader('X-RateLimit-Remaining', remaining);
      res.setHeader('X-RateLimit-Reset', Math.floor(resetTime / 1000));
    }
    
    // If rate limited, return 429 response
    if (isRateLimited) {
      const retryAfter = Math.ceil((resetTime - Date.now()) / 1000);
      res.setHeader('Retry-After', retryAfter);
      
      res.status(429).json({
        error: {
          code: 'RATE_LIMIT_EXCEEDED',
          message: rateLimitConfig.message,
          retryAfter,
        },
      });
      
      return;
    }
    
    // Track response status for skip logic
    if (rateLimitConfig.skipSuccessfulRequests || rateLimitConfig.skipFailedRequests) {
      const originalEnd = res.end;
      res.end = function(...args: any[]) {
        if (
          (rateLimitConfig.skipSuccessfulRequests && res.statusCode < 400) ||
          (rateLimitConfig.skipFailedRequests && res.statusCode >= 400)
        ) {
          store.decrement(key);
        }
        return originalEnd.apply(res, args);
      };
    }
    
    if (next) {
      await next();
    }
  };
}

/**
 * Create endpoint-specific rate limiters
 */
export function createEndpointLimiter(endpoint: string, config?: Partial<RateLimitConfig>) {
  const endpointConfig: RateLimitConfig = {
    ...defaultConfig,
    ...config,
    keyGenerator: (req) => {
      const baseKey = defaultKeyGenerator(req);
      return \`\${endpoint}:\${baseKey}\`;
    },
  };
  
  return {
    appRouter: (handler: any) => withRateLimit(handler, endpointConfig),
    pagesRouter: () => rateLimitPages(endpointConfig),
  };
}

/**
 * Tiered rate limiting based on user type
 */
export function createTieredRateLimit(tiers: {
  anonymous?: Partial<RateLimitConfig>;
  authenticated?: Partial<RateLimitConfig>;
  premium?: Partial<RateLimitConfig>;
}) {
  return {
    appRouter: (handler: any) => {
      return async (req: NextRequest, context: any) => {
        const userId = req.headers.get('x-user-id');
        const userRole = req.headers.get('x-user-role');
        
        let config: RateLimitConfig;
        if (userRole === 'premium' && tiers.premium) {
          config = { ...defaultConfig, ...tiers.premium };
        } else if (userId && tiers.authenticated) {
          config = { ...defaultConfig, ...tiers.authenticated };
        } else {
          config = { ...defaultConfig, ...tiers.anonymous };
        }
        
        return withRateLimit(handler, config)(req, context);
      };
    },
  };
}`,
};
